import { useSelector } from '@algoan/dashboard/store/store';
import { useIntl } from 'react-intl';
import { useTheme } from 'styled-components';
import { messages } from '../../modules/analysis/regular-cash-flows-analysis-messages';
import { useResize } from '@algoan/dashboard/utils';
import { S, Triangle } from './style';
import { Events } from './events';
import { Tick } from './tick';
import { latestAnalysisSelector, isAccountsLoadingSelector } from '@algoan/dashboard/store/selectors';
import { RegularCashFlow, timelineCashFlows } from './timeline-model';
import { getDueDayFromCashFlow, getDueDayFromRegularCashFlow } from './timeline-utils';

export const Timeline: React.FC = () => {
  const isAccountsLoading = useSelector(isAccountsLoadingSelector);
  const cashFlows = useSelector(latestAnalysisSelector)?.creditInsights?.cashFlows;
  const { width, elementRef: axisRef } = useResize();
  const intl = useIntl();

  const shouldHideTimeline = isAccountsLoading || typeof cashFlows === 'undefined';

  const filteredCashFlows = cashFlows
    ?.filter((cashFlow) => !!getDueDayFromCashFlow(cashFlow))
    .filter((cashFlow) => timelineCashFlows.indexOf(cashFlow.category) > -1) as RegularCashFlow[];

  // a set of each day of month which have an event
  // used to calculate distance between events
  const daysWithPoint = filteredCashFlows?.reduce(
    (daysWithPoint, cashFlow) => {
      daysWithPoint.add(getDueDayFromRegularCashFlow(cashFlow));

      return daysWithPoint;
    },
    new Set([0, 1, 15]),
  );

  let dueDays;

  if (typeof daysWithPoint !== 'undefined') {
    dueDays = Array.from(daysWithPoint).sort((dayA, dayB) => dayA - dayB);

    dueDays = dueDays.reduce<{ [key: number]: number }>((dueDays, day, index) => {
      dueDays[day] = index;

      return dueDays;
    }, {});
  }

  const daySpacing = width / (daysWithPoint?.size ?? 1);
  const theme = useTheme();

  return (
    <S.Container>
      {width !== 0 && typeof filteredCashFlows !== 'undefined' && typeof dueDays !== 'undefined' && (
        <Events cashFlows={filteredCashFlows} daySpacing={daySpacing} dueDays={dueDays} />
      )}
      <S.Axis ref={axisRef} hide={shouldHideTimeline}>
        <S.Bar />
        <Triangle fill={theme.legacy.secondary} />
      </S.Axis>
      {width !== 0 && (
        <>
          <S.Ticks hide={shouldHideTimeline}>
            <Tick
              daySpacing={daySpacing}
              day={dueDays?.[1] ?? 0}
              label={intl.formatMessage(messages.firstDayOfMonth)}
            />
            <Tick
              daySpacing={daySpacing}
              day={dueDays?.[15] ?? 15}
              label={intl.formatMessage(messages.fifteenthDayOfMonth)}
            />
          </S.Ticks>
        </>
      )}
    </S.Container>
  );
};
